import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Save, RefreshCw, Pencil, Trash2, PackageCheck, X, Send, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '../components/Modal';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { addressesApi } from '../api/client';
import type { Address, AddressRecord } from '../types';
import { releaseInFlightKey, tryAcquireInFlightKey } from '../utils/inFlightKeys';
import { hasPreparedBulkRecoveryState } from '../utils/retryApiIdentity';

const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const CANADIAN_PROVINCES = [
  { value: '', label: 'Select Province' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const emptyAddress: Address = {
  name: '',
  company: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  email: '',
  phone: '',
  country: 'US',
  gift: '',
  tshirt_size: '',
};

type SortOption = 'name' | 'city' | 'shipments' | 'recent';
type ShipmentFilter = 'all' | 'shipped' | 'not_shipped';

function AddressBookPage() {
  const { toasts, removeToast, success, error, warning } = useToast();
  const navigate = useNavigate();

  const [addresses, setAddresses] = useState<AddressRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextAddressCursor, setNextAddressCursor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [shipmentFilter, setShipmentFilter] = useState<ShipmentFilter>('all');

  // Form state
  const [formAddress, setFormAddress] = useState<Address>(emptyAddress);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const saveInFlightRef = useRef(false);
  const saveGenerationRef = useRef(0);

  // Delete modal state
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Mark shipped state
  const [markingShippedIds, setMarkingShippedIds] = useState<Set<string>>(new Set());
  const markingShippedIdsRef = useRef(new Set<string>());

  // Sync from orders state
  const [syncing, setSyncing] = useState(false);
  const [addressSyncCursor, setAddressSyncCursor] = useState<string | null>(null);

  // Selection state for bulk operations
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    loadAddresses();
    return () => {
      saveGenerationRef.current += 1;
      saveInFlightRef.current = false;
    };
  }, []);

  const loadAddresses = async (options: { append?: boolean; cursor?: string } = {}) => {
    const append = options.append === true;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const page = await addressesApi.listPage({
        limit: 200,
        cursor: options.cursor,
      });
      setAddresses((current) => {
        if (!append) return page.addresses;
        const byId = new Map(current.map((address) => [address.id, address]));
        for (const address of page.addresses) byId.set(address.id, address);
        return [...byId.values()];
      });
      setNextAddressCursor(page.next_cursor);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load addresses';
      error(append ? 'Load More Failed' : 'Load Failed', message);
      return false;
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  };

  const handleLoadMoreAddresses = async () => {
    if (!nextAddressCursor || loadingMore) return;
    await loadAddresses({ append: true, cursor: nextAddressCursor });
  };

  const filteredAddresses = useMemo(() => {
    let result = addresses;

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (addr) =>
          addr.name?.toLowerCase().includes(query) ||
          addr.company?.toLowerCase().includes(query) ||
          addr.city?.toLowerCase().includes(query) ||
          addr.state?.toLowerCase().includes(query) ||
          addr.email?.toLowerCase().includes(query)
      );
    }

    // Filter by shipment status
    if (shipmentFilter === 'shipped') {
      result = result.filter((addr) => (addr.shipment_stats?.count || 0) > 0);
    } else if (shipmentFilter === 'not_shipped') {
      result = result.filter((addr) => (addr.shipment_stats?.count || 0) === 0);
    }

    // Sort
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'city':
          return (a.city || '').localeCompare(b.city || '');
        case 'shipments':
          return (b.shipment_stats?.count || 0) - (a.shipment_stats?.count || 0);
        case 'recent':
          const aDate = a.shipment_stats?.last_shipped_at || a.created_at || '';
          const bDate = b.shipment_stats?.last_shipped_at || b.created_at || '';
          return bDate.localeCompare(aDate);
        default:
          return 0;
      }
    });

    return result;
  }, [addresses, searchQuery, sortBy, shipmentFilter]);
  const visibleAddressIds = useMemo(
    () => new Set(filteredAddresses.map((address) => address.id)),
    [filteredAddresses]
  );
  const selectedVisibleAddresses = useMemo(
    () => filteredAddresses.filter((address) => selectedIds.has(address.id)),
    [filteredAddresses, selectedIds]
  );

  // Keep selection scoped to rows the user can currently see and act on.
  useEffect(() => {
    setSelectedIds((previous) => {
      const next = new Set(Array.from(previous).filter((id) => visibleAddressIds.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleAddressIds]);

  const formRegions = formAddress.country === 'CA' ? CANADIAN_PROVINCES : US_STATES;

  const handleFormChange = (field: keyof Address, value: string) => {
    if (saveInFlightRef.current) return;
    setFormAddress((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'gift' && value !== 'Tshirt' ? { tshirt_size: '' } : {}),
      ...(field === 'country' && value !== prev.country ? { state: '' } : {}),
    }));
  };

  const handleEdit = (address: AddressRecord) => {
    if (saveInFlightRef.current) return;
    // Close add form when editing
    setShowAddForm(false);
    // Toggle accordion - if clicking same address, close it
    if (editingId === address.id) {
      setEditingId(null);
      setFormAddress(emptyAddress);
      return;
    }
    setEditingId(address.id);
    setFormAddress({
      name: address.name || '',
      company: address.company || '',
      street1: address.street1 || '',
      street2: address.street2 || '',
      city: address.city || '',
      state: address.state || '',
      zip: address.zip || '',
      email: address.email || '',
      phone: address.phone || '',
      country: address.country || 'US',
      gift: address.gift || '',
      tshirt_size: address.tshirt_size || '',
    });
  };

  const handleCancel = () => {
    if (saveInFlightRef.current) return;
    const previouslyEditingId = editingId;
    const wasAdding = showAddForm;
    setEditingId(null);
    setShowAddForm(false);
    setFormAddress(emptyAddress);
    if (previouslyEditingId) {
      window.requestAnimationFrame(() => {
        document.getElementById(`edit-address-toggle-${previouslyEditingId}`)?.focus();
      });
    } else if (wasAdding) {
      window.requestAnimationFrame(() => {
        document.getElementById('add-address-toggle')?.focus();
      });
    }
  };

  const handleAddNew = () => {
    if (saveInFlightRef.current) return;
    setEditingId(null);
    setFormAddress(emptyAddress);
    setShowAddForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saveInFlightRef.current) return;
    const submittedEditingId = editingId;
    const submittedAddress = { ...formAddress };

    if (!submittedAddress.name || !submittedAddress.street1 || !submittedAddress.city || !submittedAddress.state || !submittedAddress.zip) {
      error('Validation Error', 'Please fill in all required fields.');
      return;
    }

    if (submittedAddress.gift === 'Tshirt' && !submittedAddress.tshirt_size) {
      error('Validation Error', 'Select a T-shirt size.');
      return;
    }

    saveInFlightRef.current = true;
    const saveGeneration = ++saveGenerationRef.current;
    setSaving(true);

    try {
      if (submittedEditingId) {
        const updated = await addressesApi.update(submittedEditingId, submittedAddress);
        if (saveGeneration !== saveGenerationRef.current) return;
        setAddresses((prev) =>
          prev.map((addr) =>
            addr.id === submittedEditingId
              ? {
                  ...updated,
                  shipment_stats: updated.shipment_stats ?? addr.shipment_stats,
                }
              : addr
          )
        );
        success('Address Updated', 'The address has been updated successfully.');
      } else {
        const created = await addressesApi.create(submittedAddress);
        if (saveGeneration !== saveGenerationRef.current) return;
        setAddresses((prev) => [...prev, created]);
        success('Address Created', 'The address has been saved successfully.');
      }

      setEditingId(null);
      setShowAddForm(false);
      setFormAddress(emptyAddress);
      if (submittedEditingId) {
        window.requestAnimationFrame(() => {
          document.getElementById(`edit-address-toggle-${submittedEditingId}`)?.focus();
        });
      } else {
        window.requestAnimationFrame(() => {
          document.getElementById('add-address-toggle')?.focus();
        });
      }
    } catch (err) {
      if (saveGeneration !== saveGenerationRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to save address';
      error('Save Failed', message);
    } finally {
      if (saveGeneration === saveGenerationRef.current) {
        saveInFlightRef.current = false;
        setSaving(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteId || saveInFlightRef.current) return;

    setDeleting(true);

    try {
      await addressesApi.delete(deleteId);
      setAddresses((prev) => prev.filter((addr) => addr.id !== deleteId));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(deleteId);
        return next;
      });
      success('Address Deleted', 'The address has been removed.');
      setDeleteId(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete address';
      error('Delete Failed', message);
    } finally {
      setDeleting(false);
    }
  };

  const handleMarkShipped = async (addr: AddressRecord) => {
    if (
      saveInFlightRef.current ||
      !tryAcquireInFlightKey(markingShippedIdsRef.current, addr.id)
    ) return;
    setMarkingShippedIds(new Set(markingShippedIdsRef.current));

    try {
      await addressesApi.markShipped(addr.id);
      success('Marked as Shipped', `${addr.name} has been marked as shipped.`);
      // Reload addresses to get updated shipment stats
      await loadAddresses();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to mark as shipped';
      error('Mark Shipped Failed', message);
    } finally {
      releaseInFlightKey(markingShippedIdsRef.current, addr.id);
      setMarkingShippedIds(new Set(markingShippedIdsRef.current));
    }
  };

  const handleSyncFromOrders = async () => {
    setSyncing(true);

    try {
      const result = await addressesApi.syncFromOrders({
        limit: 100,
        cursor: addressSyncCursor || undefined,
      });
      setAddressSyncCursor(result.next_cursor);
      if (result.created > 0) {
        success(
          result.list_complete ? 'Sync Complete' : 'Sync Page Complete',
          `Imported ${result.created} address${result.created !== 1 ? 'es' : ''} from this bounded page.`
        );
        await loadAddresses();
      } else if (!result.list_complete) {
        warning(
          'More History Available',
          `Processed ${result.processed} records. Select Continue Sync to process the next page.`
        );
      } else {
        success('Sync Complete', 'No new addresses found in shipment history.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync addresses';
      error('Sync Failed', message);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (saveInFlightRef.current) return;
    if (checked) {
      setSelectedIds(new Set(filteredAddresses.map((addr) => addr.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (saveInFlightRef.current) return;
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (checked && visibleAddressIds.has(id)) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const selectedVisibleCount = selectedVisibleAddresses.length;
  const isAllSelected = filteredAddresses.length > 0 && selectedVisibleCount === filteredAddresses.length;
  const isSomeSelected = selectedVisibleCount > 0;

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (saveInFlightRef.current) return;
    const idsToDelete = selectedVisibleAddresses.map((address) => address.id);
    if (idsToDelete.length === 0) return;
    const idSet = new Set(idsToDelete);

    setBulkDeleting(true);

    try {
      const result = await addressesApi.bulkDelete(idsToDelete);
      if (!result.success || result.failed > 0) {
        const refreshed = await loadAddresses();
        warning(
          'Some Deletes Failed',
          `${result.deleted} confirmed deleted; ${result.failed} not confirmed.${result.error ? ` ${result.error}.` : ''} ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`
        );
      } else {
        setAddresses((prev) => prev.filter((addr) => !idSet.has(addr.id)));
        success('Addresses Deleted', `Successfully deleted ${result.deleted} address${result.deleted !== 1 ? 'es' : ''}.`);
      }
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete addresses';
      const refreshed = await loadAddresses();
      setSelectedIds(new Set());
      setShowBulkDeleteModal(false);
      error(
        'Bulk Delete Failed',
        `${message}. ${refreshed ? 'The list has been refreshed.' : 'Reload the page to reconcile the list.'}`
      );
    } finally {
      setBulkDeleting(false);
    }
  };

  // Bulk order handler - navigate to bulk ship page with selected addresses
  const handleBulkOrder = () => {
    if (selectedVisibleAddresses.length === 0 || saveInFlightRef.current) return;

    // Store selected address IDs in sessionStorage for the bulk ship page
    const selectedAddresses = selectedVisibleAddresses;
    try {
      const existingQueue = sessionStorage.getItem('bulkShipAddresses');
      if (existingQueue) {
        try {
          const parsed = JSON.parse(existingQueue);
          const hasPreparedRecovery = hasPreparedBulkRecoveryState(parsed);
          if (hasPreparedRecovery) {
            warning(
              'Prepared Shipments Need Attention',
              'A previous bulk queue must be retried or discarded before starting a new one.'
            );
            navigate('/bulk-ship');
            return;
          }
        } catch {
          // A malformed stale value can be safely replaced by the new selection.
        }
      }
      sessionStorage.setItem('bulkShipAddresses', JSON.stringify(selectedAddresses));
      navigate('/bulk-ship');
    } catch {
      error('Bulk Queue Unavailable', 'Browser session storage is unavailable. No shipments were started.');
    }
  };

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="intro-box">
        <strong>Manage Your Saved Addresses</strong>
        <p>
          Save frequently used shipping addresses for quick access when creating labels.
          Addresses with shipping history show statistics below.
        </p>
      </div>

      <div style={{ display: 'grid', gap: '30px' }}>
        {/* Add New Address Button/Form */}
        <div className="panel">
          <h2 className="accordion-heading">
            <button
              id="add-address-toggle"
              type="button"
              className="accordion-header"
              aria-expanded={showAddForm}
              aria-controls="add-address-form-panel"
              onClick={() => (showAddForm ? handleCancel() : handleAddNew())}
              disabled={saving}
            >
              <span className="accordion-header-label">
                <Plus size={20} aria-hidden="true" />
                Add New Address
              </span>
              {showAddForm ? (
                <ChevronUp size={20} aria-hidden="true" />
              ) : (
                <ChevronDown size={20} aria-hidden="true" />
              )}
            </button>
          </h2>

          <div
            id="add-address-form-panel"
            className={`accordion-content ${showAddForm ? 'open' : ''}`}
            hidden={!showAddForm}
          >
            {showAddForm && (
              <form onSubmit={handleSubmit} style={{ paddingTop: '20px' }} aria-busy={saving}>
              <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
              <div style={{ display: 'grid', gap: '18px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="add-addr-name">Name *</label>
                    <input
                      id="add-addr-name"
                      type="text"
                      value={formAddress.name || ''}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder="Full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-addr-company">Company</label>
                    <input
                      id="add-addr-company"
                      type="text"
                      value={formAddress.company || ''}
                      onChange={(e) => handleFormChange('company', e.target.value)}
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="add-addr-street1">Street Address *</label>
                  <input
                    id="add-addr-street1"
                    type="text"
                    value={formAddress.street1 || ''}
                    onChange={(e) => handleFormChange('street1', e.target.value)}
                    placeholder="Street address"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="add-addr-street2">Apt / Suite / Unit</label>
                  <input
                    id="add-addr-street2"
                    type="text"
                    value={formAddress.street2 || ''}
                    onChange={(e) => handleFormChange('street2', e.target.value)}
                    placeholder="Apartment, suite, unit, etc."
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="add-addr-city">City *</label>
                    <input
                      id="add-addr-city"
                      type="text"
                      value={formAddress.city || ''}
                      onChange={(e) => handleFormChange('city', e.target.value)}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-addr-country">Country *</label>
                    <select
                      id="add-addr-country"
                      value={formAddress.country || 'US'}
                      onChange={(e) => handleFormChange('country', e.target.value)}
                      required
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-addr-state">{formAddress.country === 'CA' ? 'Province' : 'State'} *</label>
                    <select
                      id="add-addr-state"
                      value={formAddress.state || ''}
                      onChange={(e) => handleFormChange('state', e.target.value)}
                      required
                    >
                      {formRegions.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-addr-zip">{formAddress.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</label>
                    <input
                      id="add-addr-zip"
                      type="text"
                      value={formAddress.zip || ''}
                      onChange={(e) => handleFormChange('zip', e.target.value.toUpperCase())}
                      placeholder="ZIP"
                      required
                      maxLength={10}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="add-addr-email">Email</label>
                    <input
                      id="add-addr-email"
                      type="email"
                      value={formAddress.email || ''}
                      onChange={(e) => handleFormChange('email', e.target.value)}
                      placeholder="Email address"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-addr-phone">Phone</label>
                    <input
                      id="add-addr-phone"
                      type="tel"
                      value={formAddress.phone || ''}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: formAddress.gift === 'Tshirt' ? '1fr 1fr' : '1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label htmlFor="add-addr-gift">Gift</label>
                    <select
                      id="add-addr-gift"
                      value={formAddress.gift || ''}
                      onChange={(e) => handleFormChange('gift', e.target.value)}
                    >
                      <option value="">Select Gift</option>
                      <option value="Scale">Scale</option>
                      <option value="Tshirt">Tshirt</option>
                      <option value="Nutrafol">Nutrafol</option>
                    </select>
                  </div>
                  {formAddress.gift === 'Tshirt' && (
                    <div className="form-group">
                      <label htmlFor="add-addr-tshirt-size">T-Shirt Size *</label>
                      <select
                        id="add-addr-tshirt-size"
                        value={formAddress.tshirt_size || ''}
                        onChange={(e) => handleFormChange('tshirt_size', e.target.value)}
                        required
                      >
                        <option value="">Select Size</option>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button
                  type="submit"
                  className={`btn btn-primary ${saving ? 'loading' : ''}`}
                  disabled={saving}
                >
                  <Plus size={18} />
                  {saving ? 'Saving...' : 'Save Address'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={handleCancel}>
                  <X size={18} />
                  Cancel
                </button>
              </div>
              </fieldset>
              </form>
            )}
          </div>
        </div>

        {/* Address List */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0 }}>
                Saved Addresses
                <span className="count-pill" style={{ marginLeft: '12px' }}>
                  {filteredAddresses.length}
                </span>
              </h2>
              <button
                className={`btn btn-secondary ${syncing ? 'loading' : ''}`}
                onClick={handleSyncFromOrders}
                disabled={syncing}
                style={{ padding: '8px 16px', fontSize: '0.9em' }}
                title="Import addresses from shipment history"
              >
                <RefreshCw size={16} />
                {syncing ? 'Syncing...' : addressSyncCursor ? 'Continue Sync' : 'Sync Shipped'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <input
                aria-label="Search saved addresses"
                type="text"
                placeholder="Search addresses..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  clearSelection();
                }}
                style={{
                  padding: '10px 14px',
                  border: '2px solid var(--champagne-dune)',
                  borderRadius: 'var(--radius-sm)',
                  minWidth: '200px',
                }}
              />
              <select
                aria-label="Filter addresses by shipment status"
                value={shipmentFilter}
                onChange={(e) => {
                  setShipmentFilter(e.target.value as ShipmentFilter);
                  clearSelection();
                }}
                style={{
                  padding: '10px 14px',
                  border: '2px solid var(--champagne-dune)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <option value="all">All Addresses</option>
                <option value="shipped">Has Shipments</option>
                <option value="not_shipped">No Shipments</option>
              </select>
              <select
                aria-label="Sort saved addresses"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                style={{
                  padding: '10px 14px',
                  border: '2px solid var(--champagne-dune)',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                <option value="name">Sort by Name</option>
                <option value="city">Sort by City</option>
                <option value="shipments">Sort by Shipments</option>
                <option value="recent">Sort by Recent</option>
              </select>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {isSomeSelected && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '12px 16px',
                background: 'var(--desert-clay-light)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '16px',
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {selectedVisibleCount} address{selectedVisibleCount !== 1 ? 'es' : ''} selected
              </span>
              <button
                className="btn btn-primary"
                style={{ padding: '8px 16px', fontSize: '0.9em' }}
                onClick={handleBulkOrder}
                disabled={saving}
              >
                <Send size={16} />
                Create Shipments
              </button>
              <button
                className="btn btn-danger"
                style={{ padding: '8px 16px', fontSize: '0.9em' }}
                onClick={() => setShowBulkDeleteModal(true)}
                disabled={saving}
              >
                <Trash2 size={16} />
                Delete Selected
              </button>
              <button
                className="btn btn-ghost"
                style={{ padding: '8px 16px', fontSize: '0.9em' }}
                onClick={clearSelection}
                disabled={saving}
              >
                <X size={16} />
                Clear
              </button>
            </div>
          )}

          {loading ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              <div className="skeleton" style={{ height: '60px' }} />
              <div className="skeleton" style={{ height: '60px' }} />
              <div className="skeleton" style={{ height: '60px' }} />
            </div>
          ) : filteredAddresses.length === 0 ? (
            <div className="empty-state">
              <p>
                {searchQuery
                  ? 'No addresses match your search.'
                  : 'No saved addresses yet. Add one above!'}
              </p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        title="Select all"
                        aria-label="Select all visible addresses"
                        disabled={saving}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th>Name</th>
                    <th>Address</th>
                    <th>City, State</th>
                    <th>Gift</th>
                    <th>Shipments</th>
                    <th>Last Shipped</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAddresses.map((addr) => (
                    <React.Fragment key={addr.id}>
                      <tr className={editingId === addr.id ? 'editing-row' : ''}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(addr.id)}
                            onChange={(e) => handleSelectOne(addr.id, e.target.checked)}
                            aria-label={`Select address for ${addr.name || 'unnamed recipient'}`}
                            disabled={saving}
                            style={{ cursor: 'pointer' }}
                          />
                        </td>
                        <td data-label="Name">
                          <strong>{addr.name}</strong>
                          {addr.company && (
                            <div style={{ fontSize: '0.85em', color: 'var(--aged-mahogany-light)' }}>
                              {addr.company}
                            </div>
                          )}
                        </td>
                        <td data-label="Address">
                          {addr.street1}
                          {addr.street2 && `, ${addr.street2}`}
                        </td>
                        <td data-label="City, State">
                          {addr.city}, {addr.state} {addr.zip}
                          {addr.country && addr.country !== 'US' ? ` ${addr.country}` : ''}
                        </td>
                        <td data-label="Gift">
                          {addr.gift
                            ? addr.gift === 'Tshirt' && addr.tshirt_size
                              ? `${addr.gift} (${addr.tshirt_size})`
                              : addr.gift
                            : '-'}
                        </td>
                        <td data-label="Shipments">
                          <span className="count-pill">
                            {addr.shipment_stats?.count || 0}
                          </span>
                        </td>
                        <td data-label="Last Shipped">
                          {formatDate(addr.shipment_stats?.last_shipped_at)}
                        </td>
                        <td data-label="Actions">
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {(addr.shipment_stats?.count || 0) === 0 && (
                              <button
                                className={`btn btn-primary ${markingShippedIds.has(addr.id) ? 'loading' : ''}`}
                                style={{ padding: '8px 12px', fontSize: '0.85em' }}
                                onClick={() => handleMarkShipped(addr)}
                                disabled={saving || markingShippedIds.has(addr.id)}
                                title="Mark as shipped"
                              >
                                <PackageCheck size={15} />
                                {markingShippedIds.has(addr.id) ? '...' : 'Shipped'}
                              </button>
                            )}
                            <button
                              id={`edit-address-toggle-${addr.id}`}
                              className={`btn ${editingId === addr.id ? 'btn-primary' : 'btn-ghost'}`}
                              style={{ padding: '8px 12px', fontSize: '0.85em' }}
                              onClick={() => handleEdit(addr)}
                              disabled={saving}
                              aria-expanded={editingId === addr.id}
                              aria-controls={`edit-address-panel-${addr.id}`}
                              title={editingId === addr.id ? 'Close edit' : 'Edit address'}
                            >
                              {editingId === addr.id ? <ChevronUp size={15} /> : <Pencil size={15} />}
                              {editingId === addr.id ? 'Close' : 'Edit'}
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '8px 12px', fontSize: '0.85em' }}
                              onClick={() => setDeleteId(addr.id)}
                              disabled={saving}
                              title="Delete address"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Accordion Edit Row */}
                      <tr className={`accordion-edit-row ${editingId === addr.id ? 'open' : ''}`}>
                        <td colSpan={8} style={{ padding: 0, border: 'none' }}>
                          <div
                            id={`edit-address-panel-${addr.id}`}
                            className={`accordion-edit-content ${editingId === addr.id ? 'open' : ''}`}
                            aria-hidden={editingId !== addr.id}
                          >
                            <form onSubmit={handleSubmit} style={{ padding: '24px' }} aria-busy={saving}>
                              <fieldset disabled={saving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
                              <h3 style={{ marginBottom: '20px', color: 'var(--copper-ember-dark)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Pencil size={18} />
                                Edit Address
                              </h3>
                              <div style={{ display: 'grid', gap: '18px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-name-${addr.id}`}>Name *</label>
                                    <input
                                      id={`edit-addr-name-${addr.id}`}
                                      type="text"
                                      value={formAddress.name || ''}
                                      onChange={(e) => handleFormChange('name', e.target.value)}
                                      placeholder="Full name"
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-company-${addr.id}`}>Company</label>
                                    <input
                                      id={`edit-addr-company-${addr.id}`}
                                      type="text"
                                      value={formAddress.company || ''}
                                      onChange={(e) => handleFormChange('company', e.target.value)}
                                      placeholder="Company name"
                                    />
                                  </div>
                                </div>

                                <div className="form-group">
                                  <label htmlFor={`edit-addr-street1-${addr.id}`}>Street Address *</label>
                                  <input
                                    id={`edit-addr-street1-${addr.id}`}
                                    type="text"
                                    value={formAddress.street1 || ''}
                                    onChange={(e) => handleFormChange('street1', e.target.value)}
                                    placeholder="Street address"
                                    required
                                  />
                                </div>

                                <div className="form-group">
                                  <label htmlFor={`edit-addr-street2-${addr.id}`}>Apt / Suite / Unit</label>
                                  <input
                                    id={`edit-addr-street2-${addr.id}`}
                                    type="text"
                                    value={formAddress.street2 || ''}
                                    onChange={(e) => handleFormChange('street2', e.target.value)}
                                    placeholder="Apartment, suite, unit, etc."
                                  />
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-city-${addr.id}`}>City *</label>
                                    <input
                                      id={`edit-addr-city-${addr.id}`}
                                      type="text"
                                      value={formAddress.city || ''}
                                      onChange={(e) => handleFormChange('city', e.target.value)}
                                      placeholder="City"
                                      required
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-country-${addr.id}`}>Country *</label>
                                    <select
                                      id={`edit-addr-country-${addr.id}`}
                                      value={formAddress.country || 'US'}
                                      onChange={(e) => handleFormChange('country', e.target.value)}
                                      required
                                    >
                                      <option value="US">United States</option>
                                      <option value="CA">Canada</option>
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-state-${addr.id}`}>{formAddress.country === 'CA' ? 'Province' : 'State'} *</label>
                                    <select
                                      id={`edit-addr-state-${addr.id}`}
                                      value={formAddress.state || ''}
                                      onChange={(e) => handleFormChange('state', e.target.value)}
                                      required
                                    >
                                      {formRegions.map((state) => (
                                        <option key={state.value} value={state.value}>
                                          {state.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-zip-${addr.id}`}>{formAddress.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</label>
                                    <input
                                      id={`edit-addr-zip-${addr.id}`}
                                      type="text"
                                      value={formAddress.zip || ''}
                                      onChange={(e) => handleFormChange('zip', e.target.value.toUpperCase())}
                                      placeholder="ZIP"
                                      required
                                      maxLength={10}
                                    />
                                  </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-email-${addr.id}`}>Email</label>
                                    <input
                                      id={`edit-addr-email-${addr.id}`}
                                      type="email"
                                      value={formAddress.email || ''}
                                      onChange={(e) => handleFormChange('email', e.target.value)}
                                      placeholder="Email address"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-phone-${addr.id}`}>Phone</label>
                                    <input
                                      id={`edit-addr-phone-${addr.id}`}
                                      type="tel"
                                      value={formAddress.phone || ''}
                                      onChange={(e) => handleFormChange('phone', e.target.value)}
                                      placeholder="Phone number"
                                    />
                                  </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: formAddress.gift === 'Tshirt' ? '1fr 1fr' : '1fr', gap: '16px' }}>
                                  <div className="form-group">
                                    <label htmlFor={`edit-addr-gift-${addr.id}`}>Gift</label>
                                    <select
                                      id={`edit-addr-gift-${addr.id}`}
                                      value={formAddress.gift || ''}
                                      onChange={(e) => handleFormChange('gift', e.target.value)}
                                    >
                                      <option value="">Select Gift</option>
                                      <option value="Scale">Scale</option>
                                      <option value="Tshirt">Tshirt</option>
                                      <option value="Nutrafol">Nutrafol</option>
                                    </select>
                                  </div>
                                  {formAddress.gift === 'Tshirt' && (
                                    <div className="form-group">
                                      <label htmlFor={`edit-addr-tshirt-size-${addr.id}`}>T-Shirt Size *</label>
                                      <select
                                        id={`edit-addr-tshirt-size-${addr.id}`}
                                        value={formAddress.tshirt_size || ''}
                                        onChange={(e) => handleFormChange('tshirt_size', e.target.value)}
                                        required
                                      >
                                        <option value="">Select Size</option>
                                        <option value="S">S</option>
                                        <option value="M">M</option>
                                        <option value="L">L</option>
                                        <option value="XL">XL</option>
                                        <option value="XXL">XXL</option>
                                      </select>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                                <button
                                  type="submit"
                                  className={`btn btn-primary ${saving ? 'loading' : ''}`}
                                  disabled={saving}
                                >
                                  <Save size={18} />
                                  {saving ? 'Saving...' : 'Update Address'}
                                </button>
                                <button type="button" className="btn btn-ghost" onClick={handleCancel}>
                                  <X size={18} />
                                  Cancel
                                </button>
                              </div>
                              </fieldset>
                            </form>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {nextAddressCursor && !loading && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                type="button"
                className={`btn btn-secondary ${loadingMore ? 'loading' : ''}`}
                onClick={handleLoadMoreAddresses}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load More Addresses'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        title="Delete Address"
      >
        <p style={{ marginBottom: '20px' }}>
          Are you sure you want to delete this address? This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            className={`btn btn-danger ${deleting ? 'loading' : ''}`}
            onClick={handleDelete}
            disabled={deleting}
          >
            <Trash2 size={16} />
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        title="Delete Selected Addresses"
      >
        <p style={{ marginBottom: '20px' }}>
          Are you sure you want to delete {selectedVisibleCount} address{selectedVisibleCount !== 1 ? 'es' : ''}? This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-ghost"
            onClick={() => setShowBulkDeleteModal(false)}
            disabled={bulkDeleting}
          >
            Cancel
          </button>
          <button
            className={`btn btn-danger ${bulkDeleting ? 'loading' : ''}`}
            onClick={handleBulkDelete}
            disabled={bulkDeleting || selectedVisibleCount === 0}
          >
            <Trash2 size={16} />
            {bulkDeleting ? 'Deleting...' : `Delete ${selectedVisibleCount}`}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default AddressBookPage;
